// Decision bookkeeping for the full-screen review surface: which reviews this
// session made, which of them U can undo, how a failed request rolls back, and
// which of an item's rating presses is the one allowed to speak.
// Pure functions over plain objects; review_surface.svelte owns the state.
//
//   decisions:  { [submissionId]: { kind: 'approved'|'rejected'|'deleted'|'skipped'|'already_reviewed', example?, curation_level? } }
//   undoStack:  submission ids of landed (or in-flight) server decisions, most recent last
//   ratings:    { [submissionId]: { seq, generation, confirmed? } } — see pressRating

// Optimistic: recorded the moment the moderator decides, before the request lands.
export const recordDecision = ({ decisions, undoStack }, id, decision) => ({
  decisions: { ...decisions, [id]: decision },
  undoStack: [...undoStack.filter((stackedId) => stackedId !== id), id]
})

export const dropDecision = ({ decisions, undoStack }, id) => {
  const next = { ...decisions }
  delete next[id]
  return { decisions: next, undoStack: undoStack.filter((stackedId) => stackedId !== id) }
}

export const skipDecision = (decisions, id) => ({ ...decisions, [id]: { kind: 'skipped' } })

// How a failed decision request rolls back: the new decisions/undoStack, whether
// the quest turned out to already hold an Example, and the toast — the server's
// message, plus the keyboard hint for getting back to the item.
export const decisionFailure = (state, id, result) => {
  if (result.networkError) {
    return { ...dropDecision(state, id), exampleExists: false, toast: { title: 'Network error', text: 'Press ← to go back.' } }
  }
  if (result.code === 'AlreadyReviewedError') {
    return {
      decisions: { ...state.decisions, [id]: { kind: 'already_reviewed' } },
      undoStack: state.undoStack.filter((stackedId) => stackedId !== id),
      exampleExists: false,
      toast: { title: result.message, text: null }
    }
  }
  return {
    ...dropDecision(state, id),
    exampleExists: result.code === 'ExampleExistsError',
    toast: { title: result.message, text: 'Press ← to go back.' }
  }
}

// U only undoes a review that actually went through.
export const undoable = (decision) => !!decision && decision.kind !== 'skipped' && decision.kind !== 'already_reviewed'

// A quest holds at most one Example, and the server rolls back the whole
// approve+example on a duplicate — so count Examples granted to sibling
// submissions earlier this session, not just the fetched value.
export const questHasExample = (quest, { exampleQuestIds, decisions, itemsById }) => {
  if (!quest) return false
  if (quest.has_example || exampleQuestIds.has(quest.id)) return true
  return Object.entries(decisions).some(
    ([id, decision]) => decision.example === true && itemsById[id]?.quest?.id === quest.id
  )
}

export const decisionCounts = (decisions) => {
  const values = Object.values(decisions)
  const count = (kind) => values.filter((d) => d.kind === kind).length
  return { approved: count('approved'), rejected: count('rejected'), deleted: count('deleted'), skipped: count('skipped') }
}

// Where the surface opens: the clicked item if it is in the queue, else the start.
export const startIndex = (ids, startId) => Math.max(0, startId ? ids.indexOf(startId) : 0)

// -- Rating presses on an approved item -------------------------------------
// Presses on one item are numbered (`seq`): only the latest press may write the
// shown level, revert it or report a failure. A review decision or an undo bumps
// the item's `generation` and forgets what the server confirmed: a press from an
// older generation is ignored outright, even if it lands fine. A superseded press
// from the current generation still records its landed level as `confirmed`,
// because the server holds it — that is the level a later failed press falls back to.

const ratingOf = (ratings, id) => ratings[id] ?? { seq: 0, generation: 0 }

export const pressRating = (ratings, id) => {
  const current = ratingOf(ratings, id)
  const next = { ...current, seq: current.seq + 1 }
  return { ratings: { ...ratings, [id]: next }, ticket: { id, seq: next.seq, generation: next.generation } }
}

export const resetRating = (ratings, id) => {
  const { seq, generation } = ratingOf(ratings, id)
  return { ...ratings, [id]: { seq: seq + 1, generation: generation + 1 } }
}

export const latestRating = (ratings, ticket) => {
  const current = ratingOf(ratings, ticket.id)
  return current.generation === ticket.generation && current.seq === ticket.seq
}

// How a landed rate request settles: 'reset' (a decision or undo made it moot),
// 'confirmed' (latest press, server agrees), 'superseded' (landed, but a newer
// press owns the display) or 'failed' (latest press, the display must revert).
export const settleRating = (ratings, ticket, { ok, level }) => {
  const current = ratingOf(ratings, ticket.id)
  if (current.generation !== ticket.generation) return { ratings, outcome: 'reset' }

  const next = ok ? { ...ratings, [ticket.id]: { ...current, confirmed: level } } : ratings
  const latest = latestRating(next, ticket)
  return { ratings: next, outcome: !latest ? 'superseded' : ok ? 'confirmed' : 'failed' }
}

// The level a failed press falls back to: what the server last confirmed this
// session, else the level the approve set, else the level the item loaded with.
export const confirmedLevel = (ratings, id, { decisions = {}, itemsById = {} } = {}) =>
  ratings[id]?.confirmed ?? decisions[id]?.curation_level ?? itemsById[id]?.curation_level ?? 'none'
